import { useClerk } from '@clerk/react-router';
import { captureException } from '@sentry/react';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { httpLink, loggerLink } from '@trpc/client';
import { useState, type ReactElement, type ReactNode } from 'react';
import superjson from 'superjson';
import { api } from '../../utils/api.ts';

export type TRPCProviderProps = {
    children: ReactNode;
};

export function TRPCProvider({ children }: TRPCProviderProps): ReactElement {
    const clerk = useClerk();

    const [queryClient] = useState(() => {
        function handleApiError(error: unknown): void {
            captureException(error);
        }
        return new QueryClient({
            queryCache: new QueryCache({ onError: handleApiError }),
            mutationCache: new MutationCache({ onError: handleApiError }),
            defaultOptions: {
                queries: { refetchOnWindowFocus: false },
            },
        });
    });

    const [trpcClient] = useState(() =>
        api.createClient({
            transformer: superjson,
            links: [
                loggerLink({
                    enabled: (opts) =>
                        import.meta.env.DEV || (opts.direction === 'down' && opts.result instanceof Error),
                }),
                httpLink({
                    url: '/api/trpc',
                    headers: async () => {
                        const token = await clerk.session?.getToken();
                        if (!clerk.session || !token) {
                            window.location.href = '/sign-in';
                            throw new Error('No active session');
                        }
                        return {
                            Authorization: `Bearer ${token}`,
                            'x-clerk-user-id': clerk.session.user.id,
                        };
                    },
                }),
            ],
        }),
    );

    return (
        <api.Provider client={trpcClient} queryClient={queryClient}>
            <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
        </api.Provider>
    );
}
